import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { verifyEmail, selectAuthLoading, selectVerificationMessage, clearError } from '../store/slices/authSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTheme } from '../contexts/ThemeContext';
import './Auth.css';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLoading = useAppSelector(selectAuthLoading);
  const verificationMessage = useAppSelector(selectVerificationMessage);
  const { theme } = useTheme();

  const logoLarge = theme === 'dark'
    ? `${process.env.PUBLIC_URL}/debriefr_logo_text_dark.png`
    : `${process.env.PUBLIC_URL}/debriefr_logo_text_transparant.png`;

  // Get token and email from URL/state
  const token = searchParams.get('token');
  const emailFromState = (location.state as any)?.email;

  // Initialize state based on whether we have a token
  const [verificationStatus, setVerificationStatus] = useState<'waiting' | 'pending' | 'success' | 'error'>(
    token ? 'pending' : 'waiting'
  );
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    // No token = user just signed up, already showing "check your email" page
    if (!token) {
      return;
    }

    // Only attempt verification once
    if (hasAttemptedRef.current) {
      return;
    }

    hasAttemptedRef.current = true;

    const performVerification = async () => {
      try {
        dispatch(clearError());
        const result = await dispatch(verifyEmail({ token }));

        if (verifyEmail.fulfilled.match(result)) {
          setVerificationStatus('success');
          // Redirect to login after 2 seconds
          setTimeout(() => {
            navigate('/login', {
              state: {
                message: 'Email verified successfully! Please sign in to continue.'
              }
            });
          }, 2000);
        } else {
          // Verification failed - show error and redirect
          setVerificationStatus('error');
          setTimeout(() => {
            navigate('/login', {
              state: {
                message: 'Please sign in to continue. If you haven\'t verified your email yet, check your inbox for the verification link.'
              }
            });
          }, 2000);
        }
      } catch (error) {
        // Any error - show error and redirect
        setVerificationStatus('error');
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Please sign in to continue. If you haven\'t verified your email yet, check your inbox for the verification link.'
            }
          });
        }, 2000);
      }
    };

    performVerification();
  }, [token, dispatch, navigate]);

  const handleLoginRedirect = () => {
    navigate('/login', {
      state: {
        message: 'Email verified successfully! Please sign in to continue.'
      }
    });
  };

  // Waiting state - user just signed up, show "check your email" message
  if (verificationStatus === 'waiting') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <img
              src={logoLarge}
              alt="Debriefr"
              style={{ width: '200px', maxWidth: '100%' }}
            />
          </div>
          <div style={{ textAlign: 'center' }}>
            <h1>Check Your Email</h1>
            <p>We've sent a verification link to:</p>
            {emailFromState && (
              <p style={{ fontWeight: 'bold', color: '#2158e8', marginBottom: '20px' }}>
                {emailFromState}
              </p>
            )}

            <div style={{ margin: '20px 0', textAlign: 'left', backgroundColor: '#dcf6ff', padding: '15px', borderRadius: '8px' }}>
              <p><strong>Next steps:</strong></p>
              <ol style={{ margin: '10px 0', paddingLeft: '20px' }}>
                <li>Check your inbox for the verification email</li>
                <li>Click the verification link in the email</li>
                <li>Return here to sign in</li>
              </ol>
              <p><small>Can't find the email? Check your spam folder.</small></p>
            </div>

            <div style={{ marginTop: '30px' }}>
              <Link to="/login" className="btn btn-primary" style={{ marginBottom: '10px', display: 'block' }}>
                Go to Sign In
              </Link>
              <p style={{ marginTop: '15px' }}>
                <Link to="/resend-verification" style={{ color: '#2158e8' }}>
                  Didn't receive the email? Resend verification
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'pending' || isLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <img
              src={logoLarge}
              alt="Debriefr"
              style={{ width: '200px', maxWidth: '100%' }}
            />
          </div>
          <div style={{ textAlign: 'center' }}>
            <LoadingSpinner size="lg" />
            <h1 style={{ marginTop: '20px' }}>Verifying Your Email</h1>
            <p>Please wait while we verify your email address...</p>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'success') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <img
              src={logoLarge}
              alt="Debriefr"
              style={{ width: '200px', maxWidth: '100%' }}
            />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h1>Email Verified Successfully!</h1>
            <p>{verificationMessage || 'Your email has been verified and your account is now active.'}</p>

            <div className="alert alert-success" style={{ margin: '20px 0' }}>
              <p><strong>Welcome to Debriefr!</strong></p>
              <p>Your account has been upgraded to the free tier. You can now access all basic features.</p>
            </div>

            <div style={{ marginTop: '30px' }}>
              <button
                onClick={handleLoginRedirect}
                className="btn btn-primary btn-full"
                style={{ marginBottom: '10px' }}
              >
                Continue to Sign In
              </button>
              <p><small>You will be redirected automatically in a few seconds...</small></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state - redirect to login
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <img
            src={logoLarge}
            alt="Debriefr"
            style={{ width: '200px', maxWidth: '100%' }}
          />
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <h1>Redirecting...</h1>
          <p>Taking you to the login page.</p>
          <p><small>The verification link may have expired or already been used.</small></p>

          <div style={{ marginTop: '30px' }}>
            <LoadingSpinner size="sm" />
            <p style={{ marginTop: '10px' }}>
              <Link to="/login" style={{ color: '#2158e8' }}>
                Click here if you're not redirected automatically
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;